/**
 * パスワードの変更(本人)。2026-09-03 再設置(発注者指示)。
 * 現在のパスワードで本人確認してから新しいものに更新する。
 * 変更した値は控え(user_secrets)にも記録する — 承認者が確認できる運用のため。
 * 2026-09-08: 初回ログイン時は自分のパスワードを決めるまで閉じられない(forced)。IDは会社が付与、パスワードは本人が設定。
 */
import { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDb, getFirebaseAuth } from '@core/lib/firebase';
import { recordPassword } from '@core/masters/secretsRepo';
import { normalizePassword } from '@core/lib/password';
import { useAuth } from './AuthProvider';

/** 通信の一時的な失敗に備えて数回やり直す(パスワード変更直後はトークンの切り替わりで最初の書き込みが失敗することがある) */
async function withRetry(fn: () => Promise<unknown>, times = 3): Promise<boolean> {
  for (let i = 0; i < times; i++) {
    try {
      await fn();
      return true;
    } catch {
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
  }
  return false;
}

export default function PasswordModal({ onClose, forced = false }: { onClose: () => void; forced?: boolean }) {
  const toast = useToast();
  const { markPasswordSet } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * 初回ログイン(forced)では「いまのパスワード」を聞かない(2026-09-10: 初期パスワードを入れ直す欄に別の値を入れて進めなくなる事例が続いた)。
   * ログイン直後なので再認証なしで変更できる。時間が経って Firebase が再認証を求めたときだけ欄を出す
   */
  const [needCurrent, setNeedCurrent] = useState(!forced);

  async function submit() {
    if (next.length < 8) {
      toast('新しいパスワードは8文字以上にしてください');
      return;
    }
    if (next !== confirm) {
      toast('新しいパスワードが一致しません');
      return;
    }
    if (needCurrent && next === current) {
      toast('いまと同じパスワードです。別のものにしてください');
      return;
    }
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user?.email) {
      toast('ログインし直してからお試しください');
      return;
    }
    setBusy(true);
    try {
      // 本人確認(パスワード変更には直近のログインが必要)。初回ログイン直後は再認証なしで試し、求められたら欄を出して入れてもらう
      if (needCurrent) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      }
      try {
        await updatePassword(user, next);
      } catch (e) {
        const code = (e as { code?: string })?.code ?? '';
        if (!needCurrent && code.includes('requires-recent-login')) {
          setNeedCurrent(true);
          toast('確認のため、ログインに使ったパスワードも入れてください');
          return;
        }
        throw e;
      }
      // パスワード変更で古いトークンが無効になるため、新しいトークンを取り直してから記録する(2026-09-09: 記録が失敗して
      // 「設定してください」が再表示され、初期パスワードでは通らなくなる事例があった)
      await user.getIdToken(true).catch(() => undefined);
      // 控えを更新(承認者が確認できる運用。2026-09-03 指示)
      const recorded = await withRetry(() => recordPassword(user.uid, next, 'self'));
      // 本人が設定済みの印(初回ログイン時の設定を求めるかどうかに使う。2026-09-08)
      const flagged = await withRetry(() =>
        updateDoc(doc(getDb(), 'users', user.uid), { password_set_at: serverTimestamp(), updated_at: serverTimestamp() }),
      );
      // 記録に失敗しても、パスワード自体は変わっているので画面上は設定済みとして進める(再表示すると初期パスワードでは通らない)
      markPasswordSet();
      if (recorded && flagged) {
        toast(forced ? 'パスワードを設定しました' : 'パスワードを変更しました');
      } else {
        toast('パスワードは変更されました(記録の更新に失敗したため、承認者へ新しいパスワードを伝えてください)');
      }
      onClose();
    } catch (e) {
      const code = (e as { code?: string })?.code ?? '';
      toast(
        code.includes('wrong-password') || code.includes('invalid-credential')
          ? forced
            ? 'いまのパスワードが違います。ログインに使ったパスワードをそのまま入れてください(すでに変更した場合は、その新しいパスワードです)'
            : 'いまのパスワードが違います'
          : code.includes('weak-password')
            ? 'パスワードが簡単すぎます。8文字以上で英数字を混ぜてください'
            : code.includes('too-many-requests')
              ? '試行回数が多すぎます。しばらく待ってからお試しください'
              : 'パスワードを変更できませんでした',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={() => (busy || forced ? undefined : onClose())} width="min(460px, 92vw)" showClose={!forced}>
      <h3 style={{ marginBottom: 4 }}>{forced ? 'パスワードを設定してください' : 'パスワードの変更'}</h3>
      <p className="hint" style={{ marginBottom: 14 }}>
        {forced
          ? '初めてのログインです。自分だけのパスワードを決めてください(8文字以上・英字と数字を混ぜる)。設定するまで他の画面は使えません。'
          : '8文字以上で、英字と数字を混ぜたものにしてください。'}
      </p>
      <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
        {needCurrent && (
          <div>
            <label>{forced ? 'いまのパスワード(ログインに使ったもの)' : 'いまのパスワード'}</label>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(normalizePassword(e.target.value))}
            />
          </div>
        )}
        <div>
          <label>新しいパスワード</label>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(normalizePassword(e.target.value))}
          />
        </div>
        <div>
          <label>新しいパスワード(確認)</label>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(normalizePassword(e.target.value))}
          />
        </div>
      </div>
      <div className="modal-actions">
        <button className="btn-green" onClick={() => void submit()} disabled={busy}>
          {busy ? '設定中…' : forced ? '設定する' : '変更する'}
        </button>
        {!forced && (
          <button className="btn-line" onClick={onClose} disabled={busy}>
            キャンセル
          </button>
        )}
      </div>
    </Modal>
  );
}
